/**
 * One unit's scope of service: the record, its history, and what can be
 * done to it next.
 *
 * The actions are the lifecycle — draft, in review, approved, retired — and
 * only the ones this person may take in this status are shown, mirroring
 * the backend's checks (see scopeActions). An action that needs a word from
 * the person (a rejection reason, change notes, a retirement confirmed)
 * opens in place under the buttons; no modal, nothing to lose focus in.
 */

import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { isCallFailure } from '../../lib/callable';
import { SCOPE_REASON_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { useScopeDetail, useScopeAction, type ScopeAction } from './useScopes';
import { AGE_GROUP_LABEL, eventLabel, formatDay, scopeActions, STATUS_LABEL, STATUS_TONE, type ScopeDetail } from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './ScopeOfService.module.css';

type Panel = 'reject' | 'newVersion' | 'retire' | null;

/** What the page says once an action has gone through. */
export const DONE_MESSAGE: Record<ScopeAction['name'], string> = {
  submitScopeForReview: 'Submitted for review.',
  approveScope: 'Approved. This is now the current scope for the unit.',
  rejectScope: 'Rejected and returned to draft.',
  retireScope: 'Retired. The unit is no longer active.',
  createNewVersion: 'New version started as a draft.',
};

function Narrative({ title, text }: { title: string; text: string | null }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {text ? <p className={own.prose}>{text}</p> : <p className={`${own.prose} ${own.empty}`}>Not recorded.</p>}
    </section>
  );
}

function Chips({ title, items, what }: { title: string; items: string[]; what: string }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {items.length ? (
        <ul className={styles.chips} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item) => (
            <li key={item} className={styles.chip}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className={`${own.prose} ${own.empty}`}>No {what} recorded.</p>
      )}
    </section>
  );
}

export function ScopeDetailPage() {
  const { scopeId = '' } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];

  const detail = useScopeDetail(orgId, scopeId);
  const act = useScopeAction(orgId, scopeId);
  const departments = useTaxonomy('department', Boolean(orgId));

  const [panel, setPanel] = useState<Panel>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const reasonId = useId();
  const notesId = useId();

  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (id: string | null) => (id ? (byId.get(id) ?? null) : null);
  }, [departments.data]);

  function run(action: ScopeAction) {
    setDone(null);
    act.mutate(action, {
      onSuccess: () => {
        setDone(DONE_MESSAGE[action.name]);
        setPanel(null);
        setReason('');
        setNotes('');
      },
    });
  }

  function submitReject(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    run({ name: 'rejectScope', reason });
  }

  if (detail.isLoading) return <Spinner label="Opening the unit" />;

  if (detail.error || !detail.data) {
    const notFound = isCallFailure(detail.error) && detail.error.code === 'not-found';
    return (
      <div className={styles.body}>
        <PageState
          title={notFound ? 'That unit is not here' : 'Could not open the unit'}
          action={{ to: '/scope-of-service', label: 'Back to scope of service' }}
          tone="alert"
        >
          {notFound
            ? 'It may have been removed, or the link may be wrong.'
            : isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }

  const { scope, versions, events } = detail.data;
  const can = scopeActions(scope.status, roles);
  const busy = act.isPending;
  const anyAction = Object.values(can).some(Boolean);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.titled}>
            <p className={styles.eyebrow}>
              {scope.unitCode} · Version {scope.version}
            </p>
            <div className={styles.titleLine}>
              <BackLink to="/scope-of-service" label="Back to scope of service" />
              <h1 className={styles.title}>{scope.unitName}</h1>
            </div>
            <p className={own.status}>
              <span className={`${styles.tag} ${styles[STATUS_TONE[scope.status]]}`}>{STATUS_LABEL[scope.status]}</span>
              {departmentName(scope.departmentId) && <span>{departmentName(scope.departmentId)}</span>}
            </p>
          </div>

          {anyAction && (
            <div className={styles.actions}>
              {can.edit && (
                <Link to={`/scope-of-service/${scope.id}/edit`} className={styles.btn}>Edit</Link>
              )}
              {can.submit && (
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                  onClick={() => run({ name: 'submitScopeForReview' })}>
                  Submit for review
                </button>
              )}
              {can.approve && (
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                  onClick={() => run({ name: 'approveScope' })}>
                  Approve
                </button>
              )}
              {can.reject && (
                <button type="button" className={styles.btn} disabled={busy}
                  aria-expanded={panel === 'reject'} onClick={() => setPanel(panel === 'reject' ? null : 'reject')}>
                  Reject…
                </button>
              )}
              {can.newVersion && (
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                  aria-expanded={panel === 'newVersion'} onClick={() => setPanel(panel === 'newVersion' ? null : 'newVersion')}>
                  New version…
                </button>
              )}
              {can.retire && (
                <button type="button" className={styles.btn} disabled={busy}
                  aria-expanded={panel === 'retire'} onClick={() => setPanel(panel === 'retire' ? null : 'retire')}>
                  Retire…
                </button>
              )}
            </div>
          )}
        </div>

        {panel === 'reject' && (
          <form className={own.panel} onSubmit={submitReject} aria-labelledby="reject-title">
            <h2 className={own.panelTitle} id="reject-title">Reject this scope</h2>
            <div className={form.field}>
              <label className={form.label} htmlFor={reasonId}>Reason</label>
              <textarea id={reasonId} className={form.textarea} rows={3} required
                maxLength={SCOPE_REASON_MAX} value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, SCOPE_REASON_MAX))}
                aria-describedby={`${reasonId}-hint`} />
              <CharCount value={reason} max={SCOPE_REASON_MAX} />
              <p className={form.hint} id={`${reasonId}-hint`}>
                The author sees this. Say what needs to change.
              </p>
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !reason.trim()}>
                {busy ? 'Rejecting…' : 'Reject and return to draft'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {panel === 'newVersion' && (
          <form className={own.panel} onSubmit={(e) => { e.preventDefault(); run({ name: 'createNewVersion', changeNotes: notes }); }}
            aria-labelledby="version-title">
            <h2 className={own.panelTitle} id="version-title">Start version {scope.version + 1}</h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              The approved version {scope.version} stays in force and is kept as a snapshot. The new
              version starts as a draft you can edit and submit.
            </p>
            <div className={form.field}>
              <label className={form.label} htmlFor={notesId}>
                What is changing <span className={form.optional}>(optional)</span>
              </label>
              <textarea id={notesId} className={form.textarea} rows={3}
                maxLength={SCOPE_REASON_MAX} value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, SCOPE_REASON_MAX))} />
              <CharCount value={notes} max={SCOPE_REASON_MAX} />
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
                {busy ? 'Starting…' : 'Start new version'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {panel === 'retire' && (
          <div className={own.panel} role="group" aria-labelledby="retire-title">
            <h2 className={own.panelTitle} id="retire-title">Retire this unit's scope?</h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              For a unit that has closed or merged. The document and its history stay on record
              as evidence; it stops being the current scope.
            </p>
            <div className={styles.actions}>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                onClick={() => run({ name: 'retireScope' })}>
                {busy ? 'Retiring…' : 'Retire'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </div>
        )}
      </header>

      <div className={styles.body}>
        {done && (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{done}</div>
        )}
        {act.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            {isCallFailure(act.error) ? act.error.message : 'That could not be done.'}
          </div>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Details</h2>
          <dl className={styles.facts}>
            <Fact term="Department" value={departmentName(scope.departmentId)} />
            <Fact term="Facilities" value={scope.facilityNames.join(', ') || null} />
            <Fact term="Location" value={scope.physicalLocation} />
            <Fact term="Beds" value={scope.bedCount === null ? null : String(scope.bedCount)} />
            <Fact term="Unit leader" value={scope.unitLeader} />
            <Fact term="Medical director" value={scope.medicalDirector} />
            <Fact term="Effective" value={scope.effectiveDate ? formatDay(scope.effectiveDate) : null} />
            <Fact term="Next review" value={scope.nextReviewDate ? formatDay(scope.nextReviewDate) : null} />
            <Fact term="Last updated" value={scope.updatedAt ? formatDay(scope.updatedAt) : null} />
            <Fact term="Created" value={scope.createdAt ? formatDay(scope.createdAt) : null} />
          </dl>
        </section>

        <Narrative title="Description" text={scope.description} />
        <Chips title="Patient populations" items={scope.patientPopulations} what="patient populations" />
        <Chips title="Age groups served" items={scope.ageGroups.map((g) => AGE_GROUP_LABEL[g])} what="age groups" />
        <Chips title="Services provided" items={scope.servicesProvided} what="services" />
        <Narrative title="Exclusions and limitations" text={scope.exclusionsLimitations} />
        <Narrative title="Hours of operation" text={scope.hoursOfOperation} />
        <Narrative title="Staffing requirements" text={scope.staffingRequirements} />
        <Narrative title="Equipment and technology" text={scope.equipmentRequirements} />
        <Chips title="Quality indicators" items={scope.qualityIndicators} what="quality indicators" />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Version history</h2>
          {versions.length === 0 ? (
            <p className={`${own.prose} ${own.empty}`}>
              No snapshots yet. A snapshot is kept each time a version is approved.
            </p>
          ) : (
            <ul className={own.timeline}>
              {versions.map((v) => (
                <li key={v.id}>
                  <span className={own.when}>{formatDay(v.createdAt)}</span>
                  <p className={own.what}>
                    Version {v.version}
                    {v.changeNotes && <span className={own.note}>{v.changeNotes}</span>}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Activity</h2>
          {events.length === 0 ? (
            <p className={`${own.prose} ${own.empty}`}>Nothing recorded yet.</p>
          ) : (
            <ul className={own.timeline}>
              {events.map((e) => (
                <li key={e.id}>
                  <span className={own.when}>{formatDay(e.createdAt)}</span>
                  <p className={own.what}>
                    {eventLabel(e.action)}
                    {e.actorEmail && <span className={own.who}>{e.actorEmail}</span>}
                    {typeof e.details?.reason === 'string' && <span className={own.note}>{e.details.reason}</span>}
                    {typeof e.details?.changeNotes === 'string' && <span className={own.note}>{e.details.changeNotes}</span>}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

function Fact({ term, value }: { term: string; value: string | null }) {
  return (
    <div>
      <dt className={styles.factTerm}>{term}</dt>
      <dd className={`${styles.factValue} ${value ? '' : styles.absent}`}>{value ?? '—'}</dd>
    </div>
  );
}

/** For tests: the scope shape the page renders from. */
export type { ScopeDetail };
